import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Activity, Clock, Bike } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { supabase } from '@/integrations/supabase/client';

interface FleetEfficiencyData {
  usagePercentage: number;
  averageTripsPerBike: number;
  averageDowntime: number; // в часах
  totalBikes: number;
  activeBikes: number;
  topBikes: Array<{
    id: string;
    model: string;
    trips: number;
  }>;
}

export const FleetEfficiencyCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<FleetEfficiencyData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchEfficiencyData();
  }, []);

  const fetchEfficiencyData = async () => {
    try {
      setLoading(true);
      
      // Получаем все велосипеды и их статистику
      const [bikesResponse, rentalsResponse] = await Promise.all([
        supabase
          .from('bikes')
          .select('id, model, status, serial_number'),
        
        supabase
          .from('rentals')
          .select('bike_id, start_date, end_date, status')
          .gte('start_date', new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()) // Последние 30 дней
      ]);

      if (bikesResponse.error) throw bikesResponse.error;
      if (rentalsResponse.error) throw rentalsResponse.error;

      const bikes = bikesResponse.data || [];
      const rentals = rentalsResponse.data || [];

      const totalBikes = bikes.length;
      
      // Находим велосипеды в pending арендах (ожидание оплаты продления)
      const bikesInPendingRentals = rentals
        .filter(rental => rental.status === 'pending')
        .map(rental => rental.bike_id);

      // Активные велосипеды = in_use + в pending арендах (без дублирования)
      const activeBikesSet = new Set([
        ...bikes.filter(bike => bike.status === 'in_use').map(bike => bike.id),
        ...bikesInPendingRentals
      ]);
      const activeBikes = activeBikesSet.size;
      const usagePercentage = totalBikes > 0 ? (activeBikes / totalBikes) * 100 : 0;

      // Подсчитываем поездки по велосипедам только для active и pending аренд
      const bikeTrips = new Map<string, number>();
      rentals
        .filter(rental => rental.status === 'active' || rental.status === 'pending')
        .forEach(rental => {
          if (rental.bike_id) {
            const currentCount = bikeTrips.get(rental.bike_id) || 0;
            bikeTrips.set(rental.bike_id, currentCount + 1);
          }
        });

      const totalTrips = Array.from(bikeTrips.values()).reduce((sum, trips) => sum + trips, 0);
      const averageTripsPerBike = totalBikes > 0 ? totalTrips / totalBikes : 0;

      // Топ-3 велосипеда по количеству поездок
      const topBikes = Array.from(bikeTrips.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([bikeId, trips]) => {
          const bike = bikes.find(b => b.id === bikeId);
          return {
            id: bikeId,
            model: bike?.model || 'Неизвестная модель',
            trips
          };
        });

      // Примерный расчет времени простоя (упрощенная версия)
      const averageDowntime = Math.max(0, 24 - (averageTripsPerBike * 2)); // предполагаем 2 часа на поездку

      setData({
        usagePercentage,
        averageTripsPerBike,
        averageDowntime,
        totalBikes,
        activeBikes,
        topBikes
      });

    } catch (err: any) {
      console.error('Ошибка при получении данных об эффективности:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center text-lg">
            <Activity className="h-5 w-5 mr-2 text-blue-500" />
            Эффективность парка
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-2 w-full" />
            <Skeleton className="h-4 w-24" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="h-full">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center text-lg">
            <Activity className="h-5 w-5 mr-2 text-blue-500" />
            Эффективность парка
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-red-500">
            <p className="text-sm">Ошибка загрузки данных</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getUsageColor = (percentage: number) => {
    if (percentage > 80) return 'bg-green-500';
    if (percentage > 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center text-lg">
          <Activity className="h-5 w-5 mr-2 text-blue-500" />
          Эффективность парка
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-2xl font-bold text-blue-600">
                {data.usagePercentage.toFixed(1)}%
              </span>
              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                <Bike className="h-3 w-3" />
                <span>{data.activeBikes}/{data.totalBikes}</span>
              </div>
            </div>
            <Progress 
              value={data.usagePercentage} 
              className="h-2"
              indicatorClassName={getUsageColor(data.usagePercentage)}
            />
            <div className="text-xs text-muted-foreground mt-1">
              Использование парка
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center space-x-1">
              <Activity className="h-3 w-3 text-green-500" />
              <span>{data.averageTripsPerBike.toFixed(1)} поездок/месяц</span>
            </div>
            <div className="flex items-center space-x-1">
              <Clock className="h-3 w-3 text-orange-500" />
              <span>{data.averageDowntime.toFixed(1)}ч простоя</span>
            </div>
          </div>

          {data.topBikes.length > 0 && (
            <div className="border-t pt-2">
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Топ велосипеды:
              </div>
              <div className="space-y-1">
                {data.topBikes.slice(0, 2).map((bike, index) => (
                  <div key={bike.id} className="flex justify-between text-xs">
                    <span className="truncate">{bike.model}</span>
                    <span className="text-green-600 font-medium">{bike.trips}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
